import React, { useEffect, useState, useCallback } from "react";
import {
  Card,
  Button,
  Spinner,
  Alert,
  Badge,
  Accordion,
  Form,
} from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import {
  Plus,
  Trash2,
  Clock,
  BookOpen,
  GripVertical,
  Save,
  CheckCircle2,
  FileText,
  Layers,
  History,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import { BackButton } from "../ui";
import "./CourseSyllabus.scss";

const CourseSyllabus = () => {
  const { id: courseId } = useParams();
  const navigate = useNavigate();

  // ── State ──
  const [course, setCourse] = useState(null);
  const [days, setDays] = useState([]);
  const [activeVersion, setActiveVersion] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [dirty, setDirty] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [activeAccordionKey, setActiveAccordionKey] = useState("0");

  // ── Fetch course + active template ──
  const fetchData = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const [courseRes, templateRes, versionsRes] = await Promise.all([
        apiClient.get(`/courses/${courseId}`),
        apiClient.get(`/syllabus/template/${courseId}`).catch(() => null),
        apiClient.get(`/syllabus/template/${courseId}/versions`).catch(() => null),
      ]);

      setCourse(courseRes.data.course);

      if (templateRes?.data?.template) {
        const t = templateRes.data.template;
        setActiveVersion(t.version);
        setDays(
          t.days.map((d) => ({
            dayNumber: d.dayNumber,
            title: d.title,
            topics: d.topics || [],
            estimatedDuration: d.estimatedDuration || 60,
          }))
        );
      } else {
        setActiveVersion(null);
        setDays([]);
      }

      if (versionsRes?.data?.templates) {
        setVersions(versionsRes.data.templates);
      }

      setDirty(false);
    } catch (e) {
      setErr("Failed to load syllabus data.");
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Auto-dismiss success messages ──
  useEffect(() => {
    if (successMsg) {
      const timer = setTimeout(() => setSuccessMsg(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMsg]);

  // ── Day CRUD ──
  const addDay = () => {
    const nextDayNumber =
      days.length > 0 ? Math.max(...days.map((d) => d.dayNumber)) + 1 : 1;
    setDays([
      ...days,
      {
        dayNumber: nextDayNumber,
        title: "",
        topics: [""],
        estimatedDuration: 60,
      },
    ]);
    setDirty(true);
    // Open the newly added accordion
    setActiveAccordionKey(String(days.length));
  };

  const removeDay = (index) => {
    const updated = days.filter((_, i) => i !== index);
    // Renumber days sequentially
    const renumbered = updated.map((d, i) => ({
      ...d,
      dayNumber: i + 1,
    }));
    setDays(renumbered);
    setDirty(true);
  };

  const updateDay = (index, field, value) => {
    const updated = [...days];
    updated[index] = { ...updated[index], [field]: value };
    setDays(updated);
    setDirty(true);
  };

  // ── Topic CRUD ──
  const addTopic = (dayIndex) => {
    const updated = [...days];
    updated[dayIndex] = {
      ...updated[dayIndex],
      topics: [...updated[dayIndex].topics, ""],
    };
    setDays(updated);
    setDirty(true);
  };

  const removeTopic = (dayIndex, topicIndex) => {
    const updated = [...days];
    updated[dayIndex] = {
      ...updated[dayIndex],
      topics: updated[dayIndex].topics.filter((_, i) => i !== topicIndex),
    };
    setDays(updated);
    setDirty(true);
  };

  const updateTopic = (dayIndex, topicIndex, value) => {
    const updated = [...days];
    const newTopics = [...updated[dayIndex].topics];
    newTopics[topicIndex] = value;
    updated[dayIndex] = { ...updated[dayIndex], topics: newTopics };
    setDays(updated);
    setDirty(true);
  };

  // ── Save / Activate ──
  const handleSave = async (activate = false) => {
    setErr("");
    setSuccessMsg("");

    // Validation
    for (const day of days) {
      if (!day.title.trim()) {
        setErr("Every day must have a title.");
        return;
      }
      const nonEmptyTopics = day.topics.filter((t) => t.trim());
      if (nonEmptyTopics.length === 0) {
        setErr(`Day ${day.dayNumber} must have at least one topic.`);
        return;
      }
    }

    setSaving(true);
    try {
      // Filter out empty topics before saving
      const cleanDays = days.map((d) => ({
        dayNumber: d.dayNumber,
        title: d.title.trim(),
        topics: d.topics.filter((t) => t.trim()),
        estimatedDuration: Number(d.estimatedDuration) || 60,
      }));

      const { data } = await apiClient.post("/syllabus/template", {
        course: courseId,
        days: cleanDays,
      });

      setActiveVersion(data.template.version);
      setDirty(false);
      setSuccessMsg(
        activate
          ? `Syllabus v${data.template.version} saved & activated!`
          : `Syllabus v${data.template.version} saved as draft.`
      );

      // Refresh versions list
      const versionsRes = await apiClient
        .get(`/syllabus/template/${courseId}/versions`)
        .catch(() => null);
      if (versionsRes?.data?.templates) {
        setVersions(versionsRes.data.templates);
      }
    } catch (e) {
      setErr(e.response?.data?.message || "Failed to save syllabus.");
    } finally {
      setSaving(false);
    }
  };

  // ── Load a specific version ──
  const loadVersion = (template) => {
    setDays(
      template.days.map((d) => ({
        dayNumber: d.dayNumber,
        title: d.title,
        topics: d.topics || [],
        estimatedDuration: d.estimatedDuration || 60,
      }))
    );
    setActiveVersion(template.version);
    setShowVersions(false);
    setDirty(false);
  };

  // ── Derived stats ──
  const totalTopics = days.reduce(
    (sum, d) => sum + d.topics.filter((t) => t.trim()).length,
    0
  );
  const totalDuration = days.reduce(
    (sum, d) => sum + (Number(d.estimatedDuration) || 0),
    0
  );

  const formatDuration = (mins) => {
    if (mins < 60) return `${mins}m`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  // ── Loading / Error States ──
  if (loading)
    return (
      <div className="text-center my-5">
        <Spinner animation="border" variant="secondary" />
      </div>
    );

  return (
    <div className="syllabus-wrapper">
      {/* Back Button and Header */}
      <div className="syllabus-back-button">
        <BackButton onClick={() => navigate(`/courses/${courseId}`)} />
        <div className="page-header-section">
          <div>
            <h1>Course Syllabus</h1>
            <p>
              Manage the day-wise syllabus template for{" "}
              <strong>{course?.name || "this course"}</strong>
            </p>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {err && (
        <Alert variant="danger" dismissible onClose={() => setErr("")}>
          {err}
        </Alert>
      )}
      {successMsg && (
        <Alert variant="success" dismissible onClose={() => setSuccessMsg("")}>
          <CheckCircle2 size={16} className="me-2" />
          {successMsg}
        </Alert>
      )}

      {/* Top Info Bar */}
      <div className="syllabus-info-bar">
        <div className="syllabus-info-left">
          <div className="syllabus-icon-box">
            <BookOpen size={24} />
          </div>
          <div>
            <h2 className="syllabus-course-name">
              {course?.name || "Course Syllabus"}
            </h2>
            <div className="syllabus-meta">
              {activeVersion && (
                <Badge className="syllabus-version-badge">
                  Active Version v{activeVersion}
                </Badge>
              )}
              <span className="syllabus-stat">
                <Layers size={14} />
                {days.length} {days.length === 1 ? "Day" : "Days"}
              </span>
              <span className="syllabus-stat">
                <FileText size={14} />
                {totalTopics} {totalTopics === 1 ? "Topic" : "Topics"}
              </span>
              <span className="syllabus-stat">
                <Clock size={14} />
                {formatDuration(totalDuration)} total
              </span>
            </div>
          </div>
        </div>
        <div className="syllabus-info-actions">
          <Button
            variant="outline-secondary"
            size="sm"
            className="version-history-btn"
            onClick={() => setShowVersions(!showVersions)}
            disabled={versions.length === 0}
          >
            <History size={16} />
            Versions ({versions.length})
          </Button>
          <Button
            variant="primary"
            size="sm"
            className="add-day-btn"
            onClick={addDay}
          >
            <Plus size={16} />
            Add Day
          </Button>
        </div>
      </div>

      {/* Version History Dropdown */}
      {showVersions && versions.length > 0 && (
        <Card className="versions-dropdown-card">
          <Card.Body className="p-0">
            <div className="versions-list">
              {versions.map((v) => (
                <div
                  key={v._id || v.version}
                  className={`version-item ${
                    v.version === activeVersion ? "version-active" : ""
                  }`}
                  onClick={() => loadVersion(v)}
                  role="button"
                  tabIndex={0}
                >
                  <div className="version-info">
                    <span className="version-label">
                      Version {v.version}
                      {v.isActive && (
                        <Badge bg="success" className="ms-2">
                          Active
                        </Badge>
                      )}
                    </span>
                    <span className="version-date">
                      {new Date(v.createdAt).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                        timeZone: "Asia/Kolkata",
                      })}
                    </span>
                  </div>
                  <span className="version-days">
                    {v.days?.length || 0} days
                  </span>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Empty State */}
      {days.length === 0 ? (
        <Card className="syllabus-empty-card">
          <Card.Body>
            <div className="syllabus-empty-state">
              <div className="empty-icon-circle">
                <BookOpen size={40} />
              </div>
              <h3>No Syllabus Days Yet</h3>
              <p>
                Start building the day-wise syllabus template for this course.
                Each day can have multiple topics with estimated durations.
              </p>
              <Button variant="primary" onClick={addDay}>
                <Plus size={16} className="me-2" />
                Add First Day
              </Button>
            </div>
          </Card.Body>
        </Card>
      ) : (
        /* Days Accordion */
        <Accordion
          activeKey={activeAccordionKey}
          onSelect={(key) => setActiveAccordionKey(key)}
          className="syllabus-accordion"
        >
          {days.map((day, dayIndex) => (
            <Accordion.Item
              key={dayIndex}
              eventKey={String(dayIndex)}
              className="syllabus-day-item"
            >
              <Accordion.Header className="syllabus-day-header">
                <div className="day-header-content">
                  <div className="day-number-badge">
                    <span>Day {day.dayNumber}</span>
                  </div>
                  <div className="day-header-info">
                    <span className="day-title-preview">
                      {day.title || "Untitled Day"}
                    </span>
                    <span className="day-topic-count">
                      {day.topics.filter((t) => t.trim()).length} topics •{" "}
                      {formatDuration(day.estimatedDuration || 0)}
                    </span>
                  </div>
                </div>
              </Accordion.Header>
              <Accordion.Body className="syllabus-day-body">
                {/* Day Title + Duration */}
                <div className="day-fields-row">
                  <div className="day-title-field">
                    <Form.Label className="field-label">Day Title</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g. Introduction to Java"
                      value={day.title}
                      onChange={(e) =>
                        updateDay(dayIndex, "title", e.target.value)
                      }
                    />
                  </div>
                  <div className="day-duration-field">
                    <Form.Label className="field-label">
                      <Clock size={14} className="me-1" />
                      Duration (min)
                    </Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      placeholder="60"
                      value={day.estimatedDuration}
                      onChange={(e) =>
                        updateDay(dayIndex, "estimatedDuration", e.target.value)
                      }
                    />
                  </div>
                </div>

                {/* Topics List */}
                <div className="topics-section">
                  <div className="topics-header">
                    <span className="field-label">Topics</span>
                    <Button
                      variant="link"
                      size="sm"
                      className="add-topic-btn"
                      onClick={() => addTopic(dayIndex)}
                    >
                      <Plus size={14} />
                      Add Topic
                    </Button>
                  </div>
                  <div className="topics-list">
                    {day.topics.map((topic, topicIndex) => (
                      <div key={topicIndex} className="topic-row">
                        <div className="topic-grip">
                          <GripVertical size={14} />
                        </div>
                        <span className="topic-number">{topicIndex + 1}.</span>
                        <Form.Control
                          type="text"
                          className="topic-input"
                          placeholder="Enter topic name..."
                          value={topic}
                          onChange={(e) =>
                            updateTopic(dayIndex, topicIndex, e.target.value)
                          }
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          className="topic-delete-btn"
                          onClick={() => removeTopic(dayIndex, topicIndex)}
                          disabled={day.topics.length <= 1}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Delete Day */}
                <div className="day-actions">
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="delete-day-btn"
                    onClick={() => removeDay(dayIndex)}
                  >
                    <Trash2 size={14} className="me-1" />
                    Delete Day
                  </Button>
                </div>
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      )}

      {/* Sticky Action Bar */}
      {days.length > 0 && (
        <div className="syllabus-action-bar">
          <div className="action-bar-info">
            {dirty && (
              <span className="unsaved-indicator">
                <span className="unsaved-dot"></span>
                Unsaved changes
              </span>
            )}
          </div>
          <div className="action-bar-buttons">
            <Button
              variant="outline-secondary"
              onClick={() => handleSave(false)}
              disabled={saving || !dirty}
            >
              {saving ? (
                <Spinner animation="border" size="sm" className="me-2" />
              ) : (
                <Save size={16} className="me-2" />
              )}
              Save Draft
            </Button>
            <Button
              variant="primary"
              onClick={() => handleSave(true)}
              disabled={saving}
            >
              {saving ? (
                <Spinner animation="border" size="sm" className="me-2" />
              ) : (
                <CheckCircle2 size={16} className="me-2" />
              )}
              Save & Activate Syllabus
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseSyllabus;
